import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { appVersionService } from "../../services/appVersion.service";
import { SaveAppVersion } from "../../domain/appVersion.interface";

/** Root key: invalidating it refreshes both the list and the current versions. */
export const APP_VERSIONS_KEY = ["appVersions"];

export const useAppVersions = () =>
	useQuery({
		queryKey: APP_VERSIONS_KEY,
		queryFn: appVersionService.getAll,
	});

/** Mounted by the drawer footer on every page, hence the 5-minute staleTime. */
export const useCurrentAppVersions = (enabled = true) =>
	useQuery({
		queryKey: [...APP_VERSIONS_KEY, "current"],
		queryFn: appVersionService.getCurrent,
		enabled,
		staleTime: 5 * 60 * 1000,
	});

const useInvalidatingMutation = <TVars, TResult>(
	mutationFn: (vars: TVars) => Promise<TResult>,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn,
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: APP_VERSIONS_KEY }),
	});
};

export const useCreateAppVersion = () =>
	useInvalidatingMutation((data: SaveAppVersion) =>
		appVersionService.create(data),
	);

export const useUpdateAppVersion = () =>
	useInvalidatingMutation(
		({ id, data }: { id: string; data: SaveAppVersion }) =>
			appVersionService.update(id, data),
	);

export const useDeleteAppVersion = () =>
	useInvalidatingMutation((id: string) => appVersionService.remove(id));

export const useSetCurrentAppVersion = () =>
	useInvalidatingMutation((id: string) => appVersionService.setCurrent(id));
